import React, { useState, useEffect } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import axiosInstance from "../../../services/axiosInstance";
import { dummyCallLogs } from "../../../utils/dummyData";


ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const BarGraph = ({ isDemoMode }) => {

  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");
  const [getCallDetails, setGetCallDetails] = useState([]);
  const [subDispositionData, setSubDispositionData] = useState({
    labels: [],
    datasets: [],
  });
  const [callsDirectionData, setCallsDirectionData] = useState({
    labels: [],
    datasets: [],
  });
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    if (!isDemoMode) {
      fetchTableData();
    } else {
      setGetCallDetails(dummyCallLogs);
    }
  }, [isDemoMode]);


  useEffect(() => {
    if (getCallDetails.length > 0) {
      generateSubDispositionChart();
      generateCallsDirectionChart();
    } else {
      setSubDispositionData({ labels: [], datasets: [] });
      setCallsDirectionData({ labels: [], datasets: [] });
    }
  }, [getCallDetails]);

  const fetchTableData = async () => {
    try {
      const res = await axiosInstance.get(
        `${apiEndPointsConfig.getAllCallLogs}/${userId}?limit=100000&export=true`,
        {
          headers: {
            Authorization: `${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setGetCallDetails(res?.data?.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setGetCallDetails([]);
    }
  };

  // Color palette for consistent styling
  const colorPalette = [
    "rgba(99, 102, 241, 0.8)", // Indigo
    "rgba(16, 185, 129, 0.8)", // Green
    "rgba(245, 158, 11, 0.8)", // Amber
    "rgba(239, 68, 68, 0.8)", // Red
    "rgba(59, 130, 246, 0.8)", // Blue
    "rgba(139, 92, 246, 0.8)", // Purple
    "rgba(236, 72, 153, 0.8)", // Pink
    "rgba(132, 204, 22, 0.8)", // Lime
    "rgba(6, 182, 212, 0.8)", // Cyan
    "rgba(249, 115, 22, 0.8)", // Orange
  ];

  const borderColorPalette = [
    "rgba(79, 70, 229, 1)",
    "rgba(5, 150, 105, 1)",
    "rgba(217, 119, 6, 1)",
    "rgba(220, 38, 38, 1)",
    "rgba(29, 78, 216, 1)",
    "rgba(124, 58, 237, 1)",
    "rgba(219, 39, 119, 1)",
    "rgba(101, 163, 13, 1)",
    "rgba(8, 145, 178, 1)",
    "rgba(234, 88, 12, 1)",
  ];

  // Generate Sub-Disposition Bar Chart
  const generateSubDispositionChart = () => {
    const counts = getCallDetails.reduce((acc, call) => {
      const subDisposition = call.payload?.childDisposition || "Unknown";
      acc[subDisposition] = (acc[subDisposition] || 0) + 1;
      return acc;
    }, {});

    const labels = Object.keys(counts);
    const values = Object.values(counts);
    const total = values.reduce((a, b) => a + b, 0);

    setSubDispositionData({
      labels,
      datasets: [
        {
          label: "Count",
          data: values,
          backgroundColor: labels.map((_, i) =>
            i === activeIndex
              ? colorPalette[i]
              : colorPalette[i % colorPalette.length]
          ),
          borderColor: labels.map(
            (_, i) => borderColorPalette[i % borderColorPalette.length]
          ),
          borderWidth: 0,
          borderRadius: 6,
          borderSkipped: false,
          hoverBackgroundColor: labels.map(
            (_, i) => colorPalette[i % colorPalette.length]
          ),
        },
      ],
      total,
    });
  };

  // ✅ Generate Calls Direction as Bar Chart (changed from Pie)
  const generateCallsDirectionChart = () => {
    const counts = getCallDetails.reduce((acc, call) => {
      const direction = call.payload?.calls_direction || "Unknown";
      acc[direction] = (acc[direction] || 0) + 1;
      return acc;
    }, {});

    const labels = Object.keys(counts);
    const values = Object.values(counts);
    const total = values.reduce((a, b) => a + b, 0);

    // Color mapping for different call directions
    const directionColors = {
      Inbound: { bg: "rgba(66, 54, 235, 0.8)", border: "rgba(66, 54, 235, 1)" },
      Outbound: {
        bg: "rgba(24, 156, 156, 0.8)",
        border: "rgba(24, 156, 156, 1)",
      },
      Internal: {
        bg: "rgba(168, 85, 247, 0.8)",
        border: "rgba(168, 85, 247, 1)",
      },
      Transfer: {
        bg: "rgba(251, 146, 60, 0.8)",
        border: "rgba(251, 146, 60, 1)",
      },
    };

    const backgroundColor = labels.map(
      (label) =>
        directionColors[label]?.bg ||
        colorPalette[Math.floor(Math.random() * colorPalette.length)]
    );

    const borderColor = labels.map(
      (label) =>
        directionColors[label]?.border ||
        borderColorPalette[
          Math.floor(Math.random() * borderColorPalette.length)
        ]
    );

    setCallsDirectionData({
      labels,
      datasets: [
        {
          label: "Count",
          data: values,
          backgroundColor,
          borderColor,
          borderWidth: 2,
          borderRadius: 6,
          borderSkipped: false,
          hoverBackgroundColor: backgroundColor,
        },
      ],
      total,
    });
  };

  const renderChartOrMessage = (
    data,
    ChartComponent,
    height = 300,
    type = ""
  ) => {
    if (!data || data.datasets.length === 0 || data.labels.length === 0) {
      return (
        <Box
          sx={{
            height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#f9f9f9",
            borderRadius: 2,
          }}
        >
          <Typography color="textSecondary">No data available</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ height }}>
        <ChartComponent
          data={data}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            animation: {
              duration: 1000,
              easing: "easeOutQuart",
            },
            interaction: {
              mode: "nearest",
              intersect: true,
            },
            plugins: {
              legend: {
                display: false, // ✅ REMOVED: Hide legend
              },
              tooltip: {
                enabled: true,
                backgroundColor: "#1f2937",
                titleColor: "#fff",
                bodyColor: "#f3f4f6",
                cornerRadius: 8,
                padding: 12,
                titleFont: { size: 13, weight: "bold" },
                bodyFont: { size: 12 },
                animation: {
                  duration: 300,
                  easing: "easeOutCubic",
                },
                callbacks: {
                  title: (context) => {
                    return context[0].label;
                  },
                  label: (context) => {
                    const total = data.total || 0;
                    const value = context.parsed.y || 0;
                    const percentage = total
                      ? ((value / total) * 100).toFixed(1)
                      : 0;
                    return `Count: ${value} (${percentage}%)`;
                  },
                  afterLabel: (context) => {
                    const total = data.total || 0;
                    return `Total: ${total}`;
                  },
                },
              },
            },
            hover: {
              mode: "nearest",
              intersect: false,
              onHover: (event, chartElement) => {
                event.native.target.style.cursor = chartElement.length
                  ? "pointer"
                  : "default";
              },
            },
            onClick: (event, elements, chart) => {
              if (elements.length > 0) {
                const { index } = elements[0];
                const label = data.labels[index];
                const value = data.datasets[0].data[index];
                setActiveIndex(index);
                console.log(`${type} - ${label}: ${value}`);
              }
            },
            scales: {
              x: {
                ticks: {
                  color: "#555",
                  font: { size: 12 },
                },
                grid: {
                  color: "#eee",
                  drawBorder: false,
                },
                border: { display: false },
              },
              y: {
                beginAtZero: true,
                ticks: {
                  color: "#555",
                  font: { size: 12 },
                  stepSize: undefined,
                },
                grid: {
                  color: "#eee",
                  drawBorder: false,
                },
                border: { display: false },
              },
            },
          }}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        {/* Sub-Disposition Bar Chart */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              p: 3,
              border: "1px solid #EEEEEE",
              borderRadius: 6,
              bgcolor: "#fff",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              transition: "all 0.3s ease",
              "&:hover": {
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              },
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 550, pb: 2 }}>
              Sub-Disposition Overview
            </Typography>
            {renderChartOrMessage(
              subDispositionData,
              Bar,
              300,
              "Sub-Disposition"
            )}
          </Box>
        </Grid>

        {/* ✅ Calls Direction Bar Chart (changed from Pie to Bar) */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              p: 3,
              border: "1px solid #EEEEEE",
              borderRadius: 6,
              bgcolor: "#fff",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              transition: "all 0.3s ease",
              "&:hover": {
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              },
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 550, pb: 2 }}>
              Calls Direction
            </Typography>
            {renderChartOrMessage(
              callsDirectionData,
              Bar,
              300,
              "Calls Direction"
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BarGraph;
